import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import api from "./api";
import { captureFirstTouch } from "./lib/acquisition";
import AuthCallback from "./components/AuthCallback";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import TipOfWeekBadge from "./components/TipOfWeekBadge";
import AnalyticsTracker from "./components/AnalyticsTracker";
import InstallPrompt from "./components/InstallPrompt";
import ExitIntent from "./components/ExitIntent";
import GetAppBanner from "./components/GetAppBanner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Route-level code splitting — keeps the initial bundle small (Core Web Vitals).
const Gallery = lazy(() => import("./pages/Gallery"));
const AIDesigner = lazy(() => import("./pages/AIDesigner"));
const MyDesigns = lazy(() => import("./pages/MyDesigns"));
const Projects = lazy(() => import("./pages/Projects"));
const Plants = lazy(() => import("./pages/Plants"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const MaterialCalculator = lazy(() => import("./pages/MaterialCalculator"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const PlantER = lazy(() => import("./pages/PlantER"));
const BuddyCare = lazy(() => import("./pages/BuddyCare"));
const SeasonalPlanner = lazy(() => import("./pages/SeasonalPlanner"));
const ProsDirectory = lazy(() => import("./pages/ProsDirectory"));
const ProsJoin = lazy(() => import("./pages/ProsJoin"));
const ProsRegister = lazy(() => import("./pages/ProsRegister"));
const ProsDashboard = lazy(() => import("./pages/ProsDashboard"));
const AdminTips = lazy(() => import("./pages/AdminTips"));
const MarketingHub = lazy(() => import("./pages/MarketingHub"));
const Privacy = lazy(() => import("./pages/Privacy"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Blog = lazy(() => import("./pages/Blog"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Referral = lazy(() => import("./pages/Referral"));
const Wall = lazy(() => import("./pages/Wall"));
const IdeasHub = lazy(() => import("./pages/IdeasHub"));
const IdeasCategory = lazy(() => import("./pages/IdeasCategory"));
const LocalFinder = lazy(() => import("./pages/LocalFinder"));
const AcademyHub = lazy(() => import("./pages/AcademyHub"));
const AcademyLesson = lazy(() => import("./pages/AcademyLesson"));
const MyBackyard = lazy(() => import("./pages/MyBackyard"));
const TownIdeas = lazy(() => import("./pages/TownIdeas"));
const TownsHub = lazy(() => import("./pages/TownsHub"));

const TOAST_OPTIONS = {
  style: {
    background: "#FFFFFF",
    border: "1px solid #E5E0D8",
    color: "#1A2F25",
    fontFamily: "Figtree, sans-serif",
  },
};

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#F9F6F0]">
      <div className="text-[#5C6B62] font-serif text-xl">Loading…</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F6F0]">
        <div className="text-[#5C6B62] font-serif text-xl">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function VisitPing() {
  useEffect(() => {
    captureFirstTouch();
    try {
      if (!sessionStorage.getItem("bb_visit_pinged")) {
        sessionStorage.setItem("bb_visit_pinged", "1");
        api.post("/track/visit").catch(() => {});
      }
    } catch { /* storage unavailable */ }
  }, []);
  return null;
}

function Shell() {
  // Synchronous check during render (useEffect is too late): if returning from
  // Google OAuth, process the session_id BEFORE any route/auth logic runs.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A2F25] flex flex-col">
      <AnalyticsTracker />
      <VisitPing />
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/ideas" element={<IdeasHub />} />
            <Route path="/ideas/:slug" element={<IdeasCategory />} />
            <Route path="/academy" element={<AcademyHub />} />
            <Route path="/academy/:slug" element={<AcademyLesson />} />
            <Route path="/find-plants-near-me" element={<LocalFinder slug="find-plants-near-me" />} />
            <Route path="/local-garden-centers" element={<LocalFinder slug="local-garden-centers" />} />
            <Route path="/landscape-materials-near-me" element={<LocalFinder slug="landscape-materials-near-me" />} />
            <Route path="/plant-nurseries-near-me" element={<LocalFinder slug="plant-nurseries-near-me" />} />
            <Route path="/landscape-suppliers-near-me" element={<LocalFinder slug="landscape-suppliers-near-me" />} />
            <Route path="/mulch-near-me" element={<LocalFinder slug="mulch-near-me" />} />
            <Route path="/topsoil-near-me" element={<LocalFinder slug="topsoil-near-me" />} />
            <Route path="/stone-gravel-near-me" element={<LocalFinder slug="stone-gravel-near-me" />} />
            <Route path="/paver-suppliers-near-me" element={<LocalFinder slug="paver-suppliers-near-me" />} />
            <Route path="/landscaping-ideas" element={<TownsHub />} />
            <Route path="/landscaping-ideas/:town" element={<TownIdeas />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/refer" element={<Referral />} />
            <Route path="/wall" element={<Wall />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Free tools */}
            <Route path="/material-calculator" element={<MaterialCalculator />} />
            <Route path="/seasonal-planner" element={<SeasonalPlanner />} />
            <Route path="/pros" element={<ProsDirectory />} />
            <Route path="/pros/join" element={<ProsJoin />} />
            <Route path="/pros/register" element={<ProsRegister />} />
            <Route path="/plants" element={<Plants />} />

            {/* Auth-required */}
            <Route path="/buddy-care" element={<ProtectedRoute><BuddyCare /></ProtectedRoute>} />
            <Route path="/ai-designer" element={<ProtectedRoute><AIDesigner /></ProtectedRoute>} />
            <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
            <Route path="/plant-er" element={<ProtectedRoute><PlantER /></ProtectedRoute>} />
            <Route path="/my-designs" element={<ProtectedRoute><MyDesigns /></ProtectedRoute>} />
            <Route path="/my-backyard" element={<ProtectedRoute><MyBackyard /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/pros/dashboard" element={<ProtectedRoute><ProsDashboard /></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/admin/tips" element={<ProtectedRoute><AdminTips /></ProtectedRoute>} />
            <Route path="/admin/marketing" element={<ProtectedRoute><MarketingHub /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <TipOfWeekBadge />
      <InstallPrompt />
      <ExitIntent />
      <GetAppBanner />
      <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
